import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useOrganization } from '../../context/OrganizationContext';
import { useTranslation } from 'react-i18next';

// isActivePlaybook viene del padre comparando contra runtime.activePlaybookId
// (el puntero real que lee el runtime) — NO usar playbook.active: el flag
// puede quedar stale y mentir sobre qué flujo está atendiendo de verdad.
export default function PlaybookEditor({ playbook, botId, onDelete, isActivePlaybook = false, onActivate }) {
  const { context } = useOrganization();
  const { t } = useTranslation('bots');
  const [expanded, setExpanded] = useState(false);
  const [localStates, setLocalStates] = useState(playbook.states || {});
  const [localName, setLocalName] = useState(playbook.name || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Helper to get bots path (org/personal)
  const getBotsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'bots']
    : ['clientes', auth.currentUser?.uid, 'bots'];

  // PHASE 3 FIX: Explicit state order to prevent Firestore key-order issues
  const [stateOrder, setStateOrder] = useState(() => {
    // Initialize from playbook.stateOrder if exists, otherwise use Object.keys fallback
    if (playbook.stateOrder && Array.isArray(playbook.stateOrder)) {
      return playbook.stateOrder;
    }
    return Object.keys(playbook.states || {});
  });

  // Get ordered list of states using explicit order array
  const statesList = stateOrder.filter(stateName => localStates[stateName]); // Filter out deleted states
  const statesCount = statesList.length;

  const savePlaybook = async () => {
    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user || !botId) return;

      // Auto-calculate 'next' field based on explicit order
      const statesWithNext = {};

      statesList.forEach((stateName, index) => {
        const stateConfig = localStates[stateName];
        const nextStateName = statesList[index + 1] || null;

        statesWithNext[stateName] = {
          ...stateConfig,
          next: stateConfig.terminal ? null : nextStateName
        };
      });

      const playbookRef = doc(db, ...getBotsPath(), botId, 'playbooks', playbook.id);
      await updateDoc(playbookRef, {
        name: localName,
        states: statesWithNext,
        stateOrder: statesList, // PHASE 3 FIX: Save explicit order
      });

      setMessage('✅ Guardado');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error saving playbook:', error);
      setMessage('❌ Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isActivePlaybook) return; // el flujo activo no se borra: activá otro antes
    if (!window.confirm(t('playbook.confirmDeleteFlow', { name: localName }))) {
      return;
    }

    try {
      setSaving(true);
      const playbookRef = doc(db, ...getBotsPath(), botId, 'playbooks', playbook.id);
      await deleteDoc(playbookRef);

      setMessage(`✅ ${t('playbook.flowDeleted')}`);

      // Call parent callback to refresh list
      if (onDelete) {
        onDelete(playbook.id);
      }
    } catch (error) {
      console.error('Error deleting playbook:', error);
      setMessage(`❌ ${t('playbook.errorDeleting')}`);
    } finally {
      setSaving(false);
    }
  };

  const addStage = () => {
    const newStageName = window.prompt(t('playbook.stageNamePrompt'), `etapa_${statesCount + 1}`);
    if (!newStageName) return;

    if (localStates[newStageName]) {
      window.alert(t('playbook.stageAlreadyExists'));
      return;
    }

    setLocalStates({
      ...localStates,
      [newStageName]: {
        objetivo: t('playbook.defaultObjective'),
        ask: t('playbook.defaultMessage'),
        capture: [],
        action: null,
        terminal: false
      }
    });

    // PHASE 3 FIX: Add to explicit order array
    setStateOrder([...stateOrder, newStageName]);
  };

  const moveStage = (stateName, direction) => {
    const currentIndex = statesList.indexOf(stateName);
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === statesList.length - 1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newList = [...statesList];
    [newList[currentIndex], newList[newIndex]] = [newList[newIndex], newList[currentIndex]];

    // PHASE 3 FIX: Update explicit order array instead of reordering object
    setStateOrder(newList);
  };

  const deleteStage = (stateName) => {
    if (!window.confirm(t('playbook.confirmDeleteStage', { name: stateName }))) return;

    const newStates = { ...localStates };
    delete newStates[stateName];
    setLocalStates(newStates);

    // PHASE 3 FIX: Remove from explicit order array
    setStateOrder(stateOrder.filter(name => name !== stateName));
  };

  const updateStateField = (stateName, field, value) => {
    setLocalStates({
      ...localStates,
      [stateName]: {
        ...localStates[stateName],
        [field]: value
      }
    });
  };

  const deletePlaybook = async () => {
    if (isActivePlaybook) return; // el flujo activo no se borra: activá otro antes
    if (!window.confirm(t('playbook.confirmDeleteFunnel', { name: localName }))) return;

    try {
      const user = auth.currentUser;
      if (!user || !botId) return;

      const playbookRef = doc(db, ...getBotsPath(), botId, 'playbooks', playbook.id);
      await deleteDoc(playbookRef);

      if (onDelete) onDelete(playbook.id);
    } catch (error) {
      console.error('Error deleting playbook:', error);
      window.alert(t('playbook.errorDeletingFunnel'));
    }
  };

  // El selector de "acción automática" se retiró en R4. Su única opción real
  // era `repo_search`, cuyo gate nunca se disparó —la FSM no avanza en
  // producción— y que R3 volvió redundante al hacer que el bot busque siempre
  // ante una pregunta informativa. Una casilla que no hace nada es una promesa
  // incumplida: el usuario la marcó y no ocurrió nada.
  return (
    <div
      className="playbook-editor"
      style={{
        border: isActivePlaybook ? '3px solid #10b981' : '1px solid #e2e8f0',
        backgroundColor: isActivePlaybook ? '#f0fdf4' : '#ffffff',
        boxShadow: isActivePlaybook ? '0 4px 12px rgba(16, 185, 129, 0.15)' : 'none',
        position: 'relative',
        marginTop: isActivePlaybook ? '20px' : '0',
        overflow: 'visible'
      }}
    >
      {isActivePlaybook && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '6px 20px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
          boxShadow: '0 2px 12px rgba(16, 185, 129, 0.4)',
          zIndex: 10
        }}>
          ✓ {t('playbook.activeBadge')}
        </div>
      )}
      <div className="playbook-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px',
        minHeight: '80px'
      }}>
        <div
          className="playbook-info"
          onClick={() => setExpanded(!expanded)}
          style={{
            cursor: 'pointer',
            flex: 1,
            minWidth: 0
          }}
        >
          <h3 style={{
            color: isActivePlaybook ? '#065f46' : '#2d3748',
            margin: '0 0 8px 0',
            fontSize: '18px'
          }}>
            {localName}
          </h3>
          <div className="playbook-meta" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center'
          }}>
            {playbook.strict && (
              <span className="badge" style={{
                backgroundColor: '#f3f4f6',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {t('playbook.strict')}
              </span>
            )}
            <span className="meta-item" style={{ fontSize: '13px', color: '#6b7280' }}>
              {statesCount} {t('playbook.stages')}
            </span>
          </div>
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          flexShrink: 0
        }}>
          {!isActivePlaybook && onActivate && statesCount > 0 && (
            <button
              onClick={onActivate}
              style={{
                padding: '8px 12px',
                backgroundColor: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              title={t('playbook.activateThisFlow')}
            >
              ▶ {t('playbook.activateButton')}
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={isActivePlaybook}
            style={{
              padding: '8px 12px',
              backgroundColor: isActivePlaybook ? '#fca5a5' : '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: isActivePlaybook ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => { if (!isActivePlaybook) e.currentTarget.style.backgroundColor = '#dc2626'; }}
            onMouseLeave={(e) => { if (!isActivePlaybook) e.currentTarget.style.backgroundColor = '#ef4444'; }}
            title={isActivePlaybook ? t('playbook.cannotDeleteActive') : t('playbook.deleteThisFlow')}
          >
            🗑️ {t('playbook.deleteButton')}
          </button>
          <button
            className="expand-btn"
            onClick={() => setExpanded(!expanded)}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            {expanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="playbook-content">
          {/* Nombre del playbook editable */}
          <div className="info-row" style={{ marginBottom: '1rem' }}>
            <strong>{t('playbook.nameLabel')}</strong>
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              style={{
                marginLeft: '10px',
                padding: '6px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                flex: 1
              }}
            />
          </div>

          <div className="info-row">
            <strong>{t('playbook.startStageLabel')}</strong> {playbook.start_state}
          </div>

          <div className="section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4>{t('playbook.funnelTitle')}</h4>
              <button onClick={addStage} style={{
                padding: '8px 16px',
                backgroundColor: '#9c27b0',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600
              }}>
                {t('playbook.addStage')}
              </button>
            </div>

            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {t('playbook.funnelDescription')}
            </p>

            <div className="states-list">
              {statesList.map((stateName, index) => {
                const stateConfig = localStates[stateName];
                return (
                <div key={stateName} className="stage-card" style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  {/* Header with stage number, name, and controls */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto', minWidth: 0 }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: '#9c27b0',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </span>
                        <strong style={{ fontSize: '16px', color: '#2d3748' }}>{stateName}</strong>
                      </div>

                      {/* Controls: Move up/down, delete */}
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <button
                          onClick={() => moveStage(stateName, 'up')}
                          disabled={index === 0}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: index === 0 ? '#e5e7eb' : '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                          title={t('playbook.moveUp')}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveStage(stateName, 'down')}
                          disabled={index === statesList.length - 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: index === statesList.length - 1 ? '#e5e7eb' : '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: index === statesList.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                          title={t('playbook.moveDown')}
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => deleteStage(stateName)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                          title={t('playbook.deleteStage')}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Objective field */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                      {t('playbook.objectiveLabel')}
                    </label>
                    <textarea
                      value={stateConfig.objetivo || ''}
                      onChange={(e) => updateStateField(stateName, 'objetivo', e.target.value)}
                      placeholder={t('playbook.objectivePlaceholder')}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: '#faf5ff',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Bot message */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                      {t('playbook.botMessageLabel')}
                    </label>
                    <textarea
                      value={stateConfig.ask || ''}
                      onChange={(e) => updateStateField(stateName, 'ask', e.target.value)}
                      placeholder={t('playbook.botMessagePlaceholder')}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                </div>
              );
              })}
            </div>
          </div>

          {message && (
            <div style={{
              padding: '10px',
              marginBottom: '10px',
              backgroundColor: message.includes('✅') ? '#d1fae5' : '#fee2e2',
              border: `1px solid ${message.includes('✅') ? '#86efac' : '#fca5a5'}`,
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}

          <div className="actions" style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button
              onClick={savePlaybook}
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {saving ? t('playbook.saving') : t('playbook.saveChanges')}
            </button>
            <button
              onClick={deletePlaybook}
              disabled={isActivePlaybook}
              title={isActivePlaybook ? t('playbook.cannotDeleteActive') : undefined}
              style={{
                padding: '10px 20px',
                backgroundColor: isActivePlaybook ? '#fca5a5' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: isActivePlaybook ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {t('playbook.deleteFunnel')}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .playbook-editor {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-bottom: 1rem;
          overflow: hidden;
        }

        .playbook-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: white;
          cursor: pointer;
          transition: background 0.2s;
        }

        .playbook-header:hover {
          background: #f7fafc;
        }

        .playbook-info h3 {
          margin: 0 0 0.5rem 0;
          color: #2d3748;
          font-size: 1.1rem;
        }

        .playbook-meta {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #edf2f7;
          color: #4a5568;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .badge.active {
          background: #c6f6d5;
          color: #22543d;
        }

        .meta-item {
          font-size: 0.875rem;
          color: #718096;
        }

        .expand-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          color: #718096;
          cursor: pointer;
          padding: 0.5rem;
        }

        .playbook-content {
          padding: 1.5rem;
          background: #f7fafc;
          border-top: 1px solid #e2e8f0;
        }

        .info-row {
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .section {
          margin-bottom: 1.5rem;
        }

        .section h4 {
          margin: 0 0 0.75rem 0;
          font-size: 0.95rem;
          color: #2d3748;
          font-weight: 600;
        }

        .states-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .state-item {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 1rem;
        }

        .state-name {
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .terminal-badge {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          background: #fed7d7;
          color: #742a2a;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .state-details {
          font-size: 0.875rem;
        }

        .state-ask {
          color: #4a5568;
          font-style: italic;
          margin: 0.25rem 0;
        }

        .state-meta {
          color: #718096;
          margin: 0.25rem 0;
          font-size: 0.8rem;
        }

        .state-meta code {
          background: #edf2f7;
          padding: 0.125rem 0.375rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.85em;
        }

        .tools-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tool-tag {
          display: inline-block;
          padding: 0.375rem 0.75rem;
          background: white;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.8rem;
          color: #2d3748;
          font-family: monospace;
        }

        .empty {
          color: #a0aec0;
          font-style: italic;
          font-size: 0.875rem;
        }

        .actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .btn-validate,
        .btn-secondary {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-validate {
          background: #667eea;
          color: white;
        }

        .btn-validate:hover {
          background: #5568d3;
        }

        .btn-secondary {
          background: white;
          color: #4a5568;
          border: 1px solid #cbd5e0;
        }

        .btn-secondary:hover {
          background: #f7fafc;
        }
      `}</style>
    </div>
  );
}
